import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useCart, type LastAdded } from "./cart-context";
import { useT } from "@/lib/i18n";
import { cdnImage, IMG } from "@/lib/img";

/**
 * Korte "toegevoegd aan winkelwagen"-melding. Verschijnt bij elke (niet-stille)
 * cart.add — bv. de groene bestelknoppen op de homepage/upsell. De configurator
 * voegt 'stil' toe (die toont al de upsell-popup), dus daar geen melding.
 */
export function CartToast() {
  const t = useT();
  const { lastAdded } = useCart();
  const [shown, setShown] = useState<LastAdded | null>(null);
  const [visible, setVisible] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!lastAdded) return;
    setShown(lastAdded);
    setVisible(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setVisible(false), 3200);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
    // alleen op een nieuwe toevoeging reageren
  }, [lastAdded?.n]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!shown) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      // Zweeft altijd 1rem bóven de sticky koopbalk (--buybar-h; 0 als er geen balk is), zodat
      // de melding op mobiel niet achter/onder de balk verdwijnt.
      style={{ bottom: "calc(var(--buybar-h, 0px) + 1rem)" }}
      className={`fixed inset-x-4 z-[60] flex justify-center transition-all duration-300 sm:inset-x-auto sm:right-6 ${
        visible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-2 opacity-0"
      }`}
    >
      <div className="flex w-full max-w-sm items-center gap-3 rounded-xl border border-primary-100 bg-white p-3 shadow-2xl">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-success-50 text-success-700">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 12l5 5L20 6" />
          </svg>
        </span>
        {shown.image && (
          <span className="hidden h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-primary-100 bg-white sm:block">
            <img src={cdnImage(shown.image, { w: IMG.thumb })} alt="" loading="lazy" decoding="async" className="h-full w-full object-contain p-0.5" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-primary-900">{t("Toegevoegd aan winkelwagen")}</p>
          <p className="truncate text-xs text-primary-600">{shown.title}</p>
        </div>
        <Link
          to="/winkelwagen"
          onClick={() => setVisible(false)}
          className="shrink-0 rounded-lg bg-buy-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-buy-700"
        >
          {t("Bekijk")}
        </Link>
      </div>
    </div>
  );
}
